import _ from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';

import { integerFormatter } from '../../../utilities/FormattingUtilities';

const PATH_ROW_ISSUES = {
    dupIp: i18n._(t`Duplicate path IPs - usually seen with VPN connection`),
    loop: i18n._(t`Path loop - this device occurs more than once, indicating a routing loop`),
    proxy: i18n._(t`Path stops at configured web proxy`),
    missingTarget: i18n._(t`Target server not reached - try other Path protocols`),
};

export const LATENCY_FORMATTER_CONFIG = {
    baseUnits: 'ms',
    ltSymbol: '<',
    ltComparison: 1,
    ltComparator: (value, config) => _.isFinite(value) && value < config.ltComparison,
};

export function locationFormatter(value, config) {
    if (!value) {
        return config?.emptyValue;
    }

    value = _.pickBy(value, (val) => {
        return val !== '?';
    });

    // Return a full location (i.e. 'city, subdivision | subdivisionIsoCode, country | isoCode, continent')
    let fullLocation = _.compact([value?.city, value?.subdivision || value?.subdivisionIsoCode]).join(', ');

    if (value.country || value.isoCode) fullLocation = `${fullLocation} (${value.country || value.isoCode})`;

    return fullLocation;
}

export function latencyFormatter(value) {
    return integerFormatter(value, LATENCY_FORMATTER_CONFIG);
}

export function hopFormatter(value) {
    return value === 0 ? '' : `${value}`;
}

function condenseUnresponsiveRows(path) {
    const keysToOmit = ['hopNumber', 'response1', 'response2', 'response3'];

    return _.chain(path)
        .reduce((hops, value) => {
            const isEmpty = _.chain(value).omit(keysToOmit).isEmpty().value();
            const count = hops.length;
            if (isEmpty && count > 0) {
                const previousIndex = count - 1;
                const previousIsEmpty = _.chain(hops[previousIndex]).omit(keysToOmit).isEmpty().value();
                if (previousIsEmpty) {
                    const startRange = _.chain(hops[previousIndex]).get('hopNumber').split('-').first().value();
                    _.set(hops[previousIndex], 'hopNumber', `${startRange}-${value?.hopNumber}`);
                    return hops;
                }
            }
            hops.push(value);
            return hops;
        }, [])
        .value();
}

function addChosenLocation(path) {
    return path.map((hop) => {
        let chosenLocation = hop?.location;
        const mostRelevantSite = hop?.sites?.[0];
        if (hop?.location?.source === 'LOCAL') {
            chosenLocation = hop?.location;
        } else if (mostRelevantSite?.city || mostRelevantSite?.province || mostRelevantSite?.country) {
            chosenLocation = mostRelevantSite;
        }

        return { ...hop, chosenLocation };
    });
}

export function filterPath(path, filters) {
    // The properties to match when filtering each hop.  These should match the columns displayed in the hop table
    // and are necessary to ensure the filter doesn't match a property that isn't displayed in the hop table.
    const filterProperties = ['hop', 'hostname', 'ipAddress', 'avgResponseTime', 'provider', 'location', 'asn'];
    const filterString = filters.map((f) => _.trim(f.value)).join(' ');
    if (!_.isEmpty(filterString)) {
        const filterTerms = filterString.toLowerCase().split(/[ ,]+/).filter(Boolean);
        return _.filter(path, (hop) => {
            const hopValues = _.chain(hop)
                .pick(filterProperties)
                .mapValues((hopValue, hopProperty) => {
                    if (hopProperty === 'location') {
                        return locationFormatter(hopValue, { summarize: true });
                    }

                    return hopValue;
                })
                .values()
                .value();
            const matchedTerms = _.filter(filterTerms, (filterTerm) => {
                return !_.isUndefined(_.find(hopValues, (hopValue) => _.includes(_.toLower(hopValue), filterTerm)));
            });

            // All terms must match for the hop to pass the filter.
            return matchedTerms.length === filterTerms.length;
        });
    }

    return path;
}

export function getTableData(data) {
    const dataClone = _.cloneDeep(data);

    return _.flow(condenseUnresponsiveRows, addChosenLocation)(dataClone?.path);
}

export function pathRowIssue(row) {
    let issue = '';

    // Pick the most important issue to show
    const issues = ['loop', 'missingTarget', 'proxy', 'dupIp'];
    _.forEach(issues, (val, i) => {
        const key = issues[i];
        if (_.get(row, key) === true) {
            issue = _.get(PATH_ROW_ISSUES, key);
            return false;
        }
    });
    return issue;
}
